
import React, { Suspense, lazy, useState, useEffect} from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import LoginPage from './LoginPage';
import RequestAccountPage from './RequestAccountPage';
import ResetPasswordPage from './ResetPasswordPage';
import HomePage from './HomePage';
import UnifiedInspectionPage from './UnifiedInspectionPage';
import ChecklistPage from './ChecklistPage';
import UploadCheckList from './UploadCheckList';
//import AdminPage from './AdminPage';
import ReportPage from './ReportPage';
import ChangePasswordPage from './ChangePasswordPage';
import AdminRoute from './AdminRoute';
import InProgressReportPage from './InProgressReportPage';
import BurndownChartPage from './BurndownChartPage';
import AdminUserManagementPage from './AdminUserManagementPage';
import AccountRequestsPage from './AccountRequestsPage';
import AircraftStatusBoard from './AircraftStatusBoard';
import AircraftAdminPage from './AircraftAdminPage';
import UploadDataPage from './UploadDataPage';
import AMPT from './AMPT';
import AircraftParkingPage from './AircraftParkingPage';
import ControlTowerPage from './ControlTowerPage';
import ReportsHubPage from './ReportsHubPage';
import ReportsPage from './ReportsPage';
import HighTimeComponentPage from './HighTimeComponentPage';
import AircraftComponentLifePage from './AircraftComponentLifePage';
import StrutWaterfallReportPage from './StrutWaterfallReportPage';
import DailyAircraftStatusReport from './DailyAircraftStatusReport';
import AircraftSummaryPage from './AircraftSummaryPage';
import SLMMPage from './SLMMPage';
import AMPTAdminPage from './AMPTAdminPage';
import ADSBAdminPage from './ADSBAdminPage';
import ExecutiveDashboard from './ExecutiveDashboard';
import NotificationsPage from './NotificationsPage';
import HangarBayLocationsPage from './HangarBayLocationsPage';
import AIPage from './AIPage';
import LegionPage from './LegionPage';
import FlightPlanningPage from './FlightPlanningPage';
import TVDashboardPage from './TVDashboardPage';
import AircraftDashboardPage from './AircraftDashboardPage';
import BayDashboardPage from './BayDashboardPage';
import ManningPage from './ManningPage';
import HistoricalCDRLs from './HistoricalCDRLs';
import AdminDashboardPage from './AdminDashboardPage';
import { METS_ENTERPRISE_ALLOWED_GROUPS } from './metsEnterpriseAccess';
import PartTracker from './PartTracker';
import PartsOnHandPage from './PartsOnHandPage';
import PartsPage from './PartsPage';

const ExecutiveCommandCenterPage = lazy(() =>
  import('./metsEnterprise/pages/ExecutiveCommandCenterPage')
);
const FleetReadinessBoardPage = lazy(() =>
  import('./metsEnterprise/pages/FleetReadinessBoardPage')
);
const AircraftDetailPage = lazy(() =>
  import('./metsEnterprise/pages/AircraftDetailPage')
);
const MaintenanceProductionPage = lazy(() =>
  import('./metsEnterprise/pages/MaintenanceProductionPage')
);
const ConstraintsDecisionsPage = lazy(() =>
  import('./metsEnterprise/pages/ConstraintsDecisionsPage')
);
const ScenarioComparisonPage = lazy(() =>
  import('./metsEnterprise/pages/ScenarioComparisonPage')
);
const ReliabilityWorkspacePage = lazy(() =>
  import('./metsEnterprise/pages/ReliabilityWorkspacePage')
);
const QualityComplianceWorkspacePage = lazy(() =>
  import('./metsEnterprise/pages/QualityComplianceWorkspacePage')
);
const MaterialWorkspacePage = lazy(() =>
  import('./metsEnterprise/pages/MaterialWorkspacePage')
);
const WorkforceWorkspacePage = lazy(() =>
  import('./metsEnterprise/pages/WorkforceWorkspacePage')
);

function MetsRouteFallback() {
  return (
    <div role="status" aria-live="polite" style={{ padding: 24 }}>
      Loading METS Enterprise workspace…
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  useEffect(() => {
    document.body.style.backgroundColor = theme === 'light' ? '#fff' : '#121212';
    document.body.style.color = theme === 'light' ? '#212529' : '#e0e0e0';
  }, [theme]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage theme={theme}/>} />
        <Route path="/request-account" element={<RequestAccountPage />} />
        <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
        <Route path="/reset-password/:uid/:token/" element={<ResetPasswordPage />} />
        {/* Public read-only TV dashboard. Auth via ?token=<TVDashboardToken>. No user login required. */}
        <Route path="/tv-dashboard" element={<TVDashboardPage />} />
        {/* Public read-only Aircraft Status board. Slice with ?start=&end= so one site can span multiple TVs. */}
        <Route path="/aircraft-dashboard" element={<AircraftDashboardPage />} />
        {/* Public read-only Bay Inspection dashboard. One TV per bay, selected via ?bay=. */}
        <Route path="/bay-dashboard" element={<BayDashboardPage />} />
        <Route path="/" element={<ProtectedRoute><HomePage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/inspections" element={<ProtectedRoute><UnifiedInspectionPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        {/* Routes accessible to all logged-in users */}
        <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage theme={theme}/></ProtectedRoute>} />
        <Route path="/inspection/:inspectionId/report" element={<ProtectedRoute><ReportPage theme={theme}/></ProtectedRoute>} />
        <Route path="/inspection/:inspectionId/progress-report" element={<ProtectedRoute><InProgressReportPage theme={theme}/></ProtectedRoute>} />
        <Route path="/inspection/:inspectionId/burndown" element={<ProtectedRoute><BurndownChartPage theme={theme}/></ProtectedRoute>} />
        {/* Routes restricted to specific groups */}
        <Route path="/inspection/:inspectionId" element={<ProtectedRoute allowedGroups={['Inspector', 'Supervisor', 'Viewer']}><ChecklistPage theme={theme} /></ProtectedRoute>} />
        {/*<Route path="/admin" element={<ProtectedRoute allowedGroups={['Supervisor']}><AdminPage /></ProtectedRoute>} />*/}
        {/* Admin-only routes */}
        {/*<Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />*/}
        <Route path="/admin-dashboard" element={<AdminRoute><AdminDashboardPage theme={theme} toggleTheme={toggleTheme} /></AdminRoute>} />
        <Route path="/upload-checklist" element={<AdminRoute><UploadCheckList theme={theme} /></AdminRoute>} />
        <Route path="/manage-users" element={<AdminRoute><AdminUserManagementPage theme={theme}/></AdminRoute>} />
        <Route path="/account-requests" element={<AdminRoute><AccountRequestsPage theme={theme}/></AdminRoute>} />
        <Route path="/status-board" element={<ProtectedRoute><AircraftStatusBoard theme={theme} /></ProtectedRoute>} />
        <Route path="/historical-cdrls" element={<ProtectedRoute><HistoricalCDRLs theme={theme} /></ProtectedRoute>} />
        <Route path="/part-tracking" element={<ProtectedRoute><PartTracker theme={theme} /></ProtectedRoute>} />
        <Route path="/upload-data" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor', 'Upload']}><UploadDataPage theme={theme} /></ProtectedRoute>} />
        <Route path="/parts" element={<ProtectedRoute><PartsPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/aircraft-admin" element={<AdminRoute><AircraftAdminPage theme={theme} /></AdminRoute>} />
        <Route path="/maintenance-priority" element={<ProtectedRoute allowStaff={true} allowedGroups={['AMPT']}><AMPT theme={theme} /></ProtectedRoute>} />
        <Route path="/aircraft-parking" element={<ProtectedRoute allowStaff={true} allowedGroups={['AMSS']}><AircraftParkingPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/control-tower" element={<ProtectedRoute allowStaff={true} allowedGroups={['AMSS']}><ControlTowerPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/hangar-bays" element={<ProtectedRoute allowStaff={true} allowedGroups={['AMSS']}><HangarBayLocationsPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><ReportsHubPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/reports/high-time-component" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><HighTimeComponentPage theme={theme} /></ProtectedRoute>} />
        <Route path="/reports/aircraft-component-life" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><AircraftComponentLifePage theme={theme} /></ProtectedRoute>} />
        <Route path="/reports/phase-waterfall" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><ReportsPage theme={theme} /></ProtectedRoute>} />
        <Route path="/reports/strut-waterfall" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><StrutWaterfallReportPage theme={theme} /></ProtectedRoute>} />
        <Route path="/daily-status-report" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><DailyAircraftStatusReport theme={theme} /></ProtectedRoute>} />
        <Route path="/aircraft-summary" element={<ProtectedRoute><AircraftSummaryPage theme={theme} /></ProtectedRoute>} />
        <Route path="/slmm" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><SLMMPage theme={theme} /></ProtectedRoute>} />
        <Route path="/ampt-admin" element={<ProtectedRoute allowStaff={true}><AMPTAdminPage theme={theme} /></ProtectedRoute>} />
        <Route path="/adsb-admin" element={<ProtectedRoute allowStaff={true}><ADSBAdminPage theme={theme} /></ProtectedRoute>} />
        <Route path="/executive-dashboard" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor']}><ExecutiveDashboard theme={theme} /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage theme={theme} /></ProtectedRoute>} />
        <Route path="/ai" element={<ProtectedRoute allowStaff={true} allowedGroups={['AI']}><AIPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        <Route path="/legion" element={<ProtectedRoute allowStaff={true} allowedGroups={['AI']}><LegionPage theme={theme} /></ProtectedRoute>} />
        <Route path="/flight-planning" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor', 'AMPT', 'Scheduler', 'Flight Planning']}><FlightPlanningPage theme={theme} /></ProtectedRoute>} />
        <Route path="/manning" element={<ProtectedRoute allowStaff={true} allowedGroups={['Supervisor', 'Manning']}><ManningPage theme={theme} /></ProtectedRoute>} />
        <Route path="/parts-on-hand" element={<ProtectedRoute><PartsOnHandPage theme={theme} toggleTheme={toggleTheme} /></ProtectedRoute>} />
        {/* METS Enterprise — staff or groups in METS_ENTERPRISE_ALLOWED_GROUPS */}
        <Route
          path="/mets"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <ExecutiveCommandCenterPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/fleet"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <FleetReadinessBoardPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/fleet/:aircraftId"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <AircraftDetailPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/maintenance"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <MaintenanceProductionPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/constraints"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <ConstraintsDecisionsPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/scenarios"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <ScenarioComparisonPage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/reliability"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <ReliabilityWorkspacePage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/quality"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <QualityComplianceWorkspacePage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/material"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <MaterialWorkspacePage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
        <Route
          path="/mets/workforce"
          element={(
            <ProtectedRoute allowStaff={true} allowedGroups={[...METS_ENTERPRISE_ALLOWED_GROUPS]}>
              <Suspense fallback={<MetsRouteFallback />}>
                <WorkforceWorkspacePage theme={theme} toggleTheme={toggleTheme} />
              </Suspense>
            </ProtectedRoute>
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
